import { type LucideIcon, SaveIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { SlotSheetActions } from './slot-sheet-shell';

/**
 * Header of a slot-creation sheet: an accent icon, the sheet title and one line
 * explaining what the form produces.
 *
 * @param props.icon - the icon rendered before the title
 * @param props.iconClassName - accent color applied to the icon
 * @param props.title - the sheet title
 * @param props.description - one line describing what will be created
 * @returns the sheet header element
 */
export const SlotFormSheetHeader = ({
  icon: Icon,
  iconClassName,
  title,
  description,
}: {
  icon: LucideIcon;
  iconClassName?: string;
  title: string;
  description: string;
}) => (
  <SheetHeader className="gap-1 border-b p-6 pr-12">
    <SheetTitle className="flex items-center gap-2 text-base">
      <Icon
        className={cn('size-4', iconClassName ?? 'text-primary')}
        aria-hidden="true"
      />
      {title}
    </SheetTitle>
    <SheetDescription className="text-xs">{description}</SheetDescription>
  </SheetHeader>
);

/**
 * Action bar of a slot-creation sheet: reset on the left, save on the right
 * with the number of slots about to be created.
 *
 * @param props.onReset - restores the form to its initial values
 * @param props.onSubmit - submits the form
 * @param props.submitting - whether the submit request is in flight
 * @param props.selectedCount - number of dates picked, shown on the save button
 * @returns the action bar element
 */
export const SlotFormSheetActions = ({
  onReset,
  onSubmit,
  submitting,
  selectedCount,
}: {
  onReset: () => void;
  onSubmit: () => void;
  submitting: boolean;
  selectedCount: number;
}) => {
  const { t } = useTranslation();

  return (
    <SlotSheetActions className="flex items-center justify-end gap-2">
      <Button
        type="button"
        variant="ghost"
        onClick={onReset}
        disabled={submitting}
      >
        {t('slot-sheet.form.reset')}
      </Button>
      <Button
        type="submit"
        onClick={onSubmit}
        loading={submitting}
        disabled={selectedCount === 0}
      >
        <SaveIcon
          className="size-4"
          aria-hidden="true"
        />
        {t('btn.save')}
        {selectedCount > 0 && (
          <span className="text-primary-foreground/80">({selectedCount})</span>
        )}
      </Button>
    </SlotSheetActions>
  );
};
